/**
 * External dependencies.
 */
import type { GetStaticProps, GetStaticPaths } from "next";

/**
 * Internal dependencies.
 */
import { fetchAPI, getLocale, getSlug, loadTranslation } from "utils";
import Layout from "layout/layout";
import ThemeTop from "blocks/theme-top/theme-top";
import DynamicSection from "blocks/dynamic-section/dynamic-section";
import SchemaSet from "components/schema-set/schema-set";
import SEO from "components/seo/seo";
import HreflangControlled from "blocks/hreflang/hreflang-controlled";
import useThemesId from "hooks/use-themes-id";

const Theme = ({ data }: any) => {
  const hreflangs = useThemesId(data);

  return (
    <Layout title="Details" desc="Details" light notice hreflang={false}>
      <HreflangControlled localizations={hreflangs} />
      <SEO data={data.attributes.SEO} />
      <SchemaSet
        schemaData={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: data.attributes.Title,
        }}
      />
      <ThemeTop data={data.attributes} app />
      <DynamicSection data={data.attributes.DynamicSections} />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const { data } = await fetchAPI(`/themes`, {
    populate: {
      Variants: {
        populate: {
          Images: "*",
        },
      },
      DetailsList: "*",
      HelpfulLinks: "*",
      BuyButton: "*",
      DemoButton: "*",
      DynamicSections: {
        populate: {
          Link: "*",
          Icon: "*",
          store: {
            populate: {
              Image: "*"
            }
          },
          stores: "*",
          PromiseBoxes: {
            populate: {
              Icon: "*",
            },
          },
          ShopSection: {
            populate: {
              Image: "*",
            },
          },
          BoxItem: {
            populate: {
              Media: "*",
            },
          },
          VideoBlock: {
            populate: {
              VideoPlaceholder: "*",
            },
          },
          Image: "*",
          FeatureBoxes: {
            populate: {
              Media: "*",
            },
          },
        },
      },
      SEO: {
        populate: "*",
      },
      localizations: "*",
    },
    locale,
    filters: {
      $or: [
        {
          slug_overwrite: ctx.params?.id,
        },
        {
          slug: ctx.params?.id,
        },
      ],
      
    },
  });

  return {
    props: {
      translation,
      data: data.pop(),
    },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  let paths: any = [];
  const { data } = await fetchAPI("/themes", {
    populate: "*",
  });

  data?.map(({ attributes }: any) => {
    if (attributes.localizations.data.length) {
      attributes.localizations.data.map((item: any) => {
        paths.push({
          params: { id: getSlug(item.attributes) },
          locale: item.attributes.locale.toLowerCase(),
        });
      });
    }
    paths.push({
      params: { id: getSlug(attributes) },
      locale: attributes.locale.toLowerCase(),
    });
  });

  return {
    paths,
    fallback: false,
  };
};

export default Theme;
